// competitive-intelligence-section.tsx
//
// Competitive Intelligence v2 (UPM-152). Replaces the old section entirely.
//
// This is a SECTION mounted under the shell contract, not a page: the mock's
// sticky review bar, its own max-width and its page padding do not exist here.
// It uses the product's section-header pattern and inherits the one gutter
// from .shell-page. See components/shell/SHELL_CONTRACT.md.
//
// Everything is real data from the Phase 11 layer:
//   * two-plane ranking feed (client to one decimal, competitors to whole
//     numbers, nulls honest)
//   * why_ahead reasons per competitor (Phase 11c), inline expansion
//
// Attribute rankings (the tabbed attribute card, its evidence drawer and its
// "what to do about it" block) used to be block 2 of this section. It MOVED to
// the Deep Signal Competitive tab — see
// competitive-intelligence/attribute-rankings-section.tsx. Nothing of it is
// rendered or imported here any more; this section is the ranking block only.
//
// One behaviour is locked by the design and must not drift:
//   Movement renders only once two consecutive reads confirm a direction.
//   Before that it is quietly EMPTY, never a zero or a dash.
import { useEffect, useMemo, useState } from "react";
import {
  fetchMeasuredCompetitiveV2, formatMovement, formatRate,
  type MeasuredCompetitiveV2, type RankingRow,
} from "@/app/services/brand/competitive-intelligence-api";
import { useEntitlements, stateOf } from "@/app/components/navigation/use-entitlements";
import { LockedFeatureModal } from "@/app/components/navigation/locked-feature-modal";
import { deepSignalTarget } from "@/app/components/deep-signal/nav-target";
import { CompetitiveInsightRail } from "./competitive-insight-rail";
import { useInsightRailData } from "./use-insight-rail-data";
import { SectionHead } from "./section-head";
import "./competitive-intelligence.css";

interface Props {
  brandName?: string;
  /** Deep-links into the Deep Signal Competitive tab from the insight rail. */
  onNavigate?: (page: string, highlight?: string) => void;
}

type Block1View = "table" | "funnel";

export function CompetitiveIntelligenceSection({ brandName, onNavigate }: Props) {
  const [feed, setFeed] = useState<MeasuredCompetitiveV2 | null>(null);
  const [failed, setFailed] = useState(false);

  const [block1View, setBlock1View] = useState<Block1View>("table");
  const [rankingInfoOpen, setRankingInfoOpen] = useState(false);
  const [expandedComp, setExpandedComp] = useState<string | null>(null);
  const [lockOpen, setLockOpen] = useState(false);

  // The rail's cards, and whether they may link. Deep Signal is the
  // destination, so its entitlement decides the click: when it is not live the
  // cards render without a click affordance and the "See all" link is dropped,
  // rather than offering a route the viewer would only bounce off.
  const insightViews = useInsightRailData(brandName, feed);
  const { states } = useEntitlements();
  const deepSignalLive = stateOf(states, "deep-signal") === "live";

  const openInDeepSignal = (attribute?: string) => {
    if (!deepSignalLive) { setLockOpen(true); return; }
    onNavigate?.("deep-signal", deepSignalTarget("competitive", attribute));
  };

  useEffect(() => {
    const brand = (brandName || "").trim();
    if (!brand) return;
    let alive = true;
    setFailed(false);
    fetchMeasuredCompetitiveV2(brand)
      .then((res) => { if (alive) setFeed(res); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [brandName]);

  const ranking = feed?.ranking;
  const client = ranking?.client;
  const competitors = ranking?.competitors ?? [];
  const measured = feed?.source === "measurement" && !!ranking;

  // The design's movement gate: only when a direction is confirmed by two
  // consecutive reads. The feed returns null deltas until then.
  const showMovement = useMemo(
    () => competitors.some((c) => formatMovement(c.deltas?.primary_rec) !== "")
      || formatMovement(client?.deltas?.primary_rec) !== "",
    [competitors, client],
  );

  const focal = feed?.focalName || brandName || "";

  if (failed) {
    return (
      <section className="ci">
        <SectionHead title="Recommendation ranking" />
        <p className="ci-muted">Competitive intelligence could not be loaded.</p>
      </section>
    );
  }

  // No measured competitive data: the honest empty state, with the inert
  // table behind it so the shape of what is coming is legible. This says the
  // read is not here yet and nothing about the competitor list: the feed cannot
  // tell those apart, and the list is set at onboarding, not by this viewer.
  if (!measured) {
    return (
      <section className="ci">
        <SectionHead title="Recommendation ranking" />
        <div className="ci-empty">
          <div className="ci-empty-title">Ranking not available yet</div>
          <div className="ci-empty-body">
            This ranking appears once a measurement read of {focal || "this brand"} has been scored.
          </div>
        </div>
        <div className="ci-inert">
          <div className="ci-thead ci-thead--inert">
            <div>Competitor</div><div className="r">Primary rec.</div>
            <div className="r">Top 5</div><div className="r">Mention</div>
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="ci-inert-row"><span className="ci-inert-bar" /></div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="ci">
      {/* ── Block 1: recommendation ranking ──────────────────────────── */}
      <SectionHead
        title="Recommendation ranking"
        onInfo={() => setRankingInfoOpen((v) => !v)}
        right={
          <>
            <span className="ci-read">
              {feed?.run?.sequence ? `Read ${feed.run.sequence}` : ""}
            </span>
            <Segmented
              value={block1View}
              onChange={(v) => { setBlock1View(v as Block1View); setExpandedComp(null); }}
              options={[{ id: "table", label: "Table" }, { id: "funnel", label: "Funnel" }]}
            />
          </>
        }
      />

      {rankingInfoOpen && (
        <div className="ci-info-callout">
          Rates come from this account's own measurement runs: every stored model response is
          classified as a primary recommendation, a top five placement, or a mention.
          Competitor rates are counted from the same responses, so they are reported to whole
          numbers while the measured brand is reported to one decimal. Movement stays empty
          until two consecutive reads agree on the direction.
        </div>
      )}

      {block1View === "table" ? (
        <div className="ci-rank">
          <div className="ci-table">
            <div className={`ci-thead${showMovement ? " ci-thead--mv" : ""}`}>
              <div>Competitor</div>
              <div className="r">Primary rec.</div>
              <div className="r">Top 5</div>
              <div className="r">Mention</div>
              {showMovement && <div className="r">Movement</div>}
              <div />
            </div>
            {competitors.map((c, i) => (
              <CompetitorRow
                key={c.brand}
                row={c}
                rank={i + 1}
                showMovement={showMovement}
                expanded={expandedComp === c.brand}
                onToggle={() => setExpandedComp(expandedComp === c.brand ? null : c.brand)}
              />
            ))}
          </div>

          {/* The client plane, visually separated because it is a different
              instrument, not a better competitor. */}
          <aside className="ci-client">
            <div className="ci-client-head">
              <span className="ci-client-name">{client?.brand}</span>
              <span className="ci-chip-teal">Measured brand</span>
            </div>
            <div className="ci-client-metrics">
              {([["Primary rec.", client?.primary_rec, client?.deltas?.primary_rec],
                 ["Top 5", client?.top5, client?.deltas?.top5],
                 ["Mention", client?.mention, client?.deltas?.mention]] as const).map(
                ([label, v, d]) => (
                  <div key={label} className="ci-metric">
                    <div className="ci-metric-label">{label}</div>
                    <div className="ci-metric-value">
                      <span>{formatRate(v, "client") ?? "—"}</span>
                      <span className="ci-metric-pct">%</span>
                    </div>
                    {formatMovement(d) && (
                      <div className="ci-mono-sm">{formatMovement(d)} vs last read</div>
                    )}
                  </div>
                ),
              )}
            </div>
          </aside>
        </div>
      ) : (
        <FunnelView client={client} competitors={competitors} showMovement={showMovement} />
      )}

      {/* Insight rail: the glanceable read on this table, linking into Deep
          Signal's Competitive tab for the full picture. Cards that cannot be
          computed are absent rather than padded. */}
      <CompetitiveInsightRail
        views={insightViews}
        linkable={deepSignalLive}
        onSelect={(attribute) => openInDeepSignal(attribute)}
        onSeeAll={() => openInDeepSignal()}
      />

      {lockOpen && (
        <LockedFeatureModal feature="deep-signal" onClose={() => setLockOpen(false)} />
      )}
    </section>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────

function Segmented({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: { id: string; label: string }[];
}) {
  return (
    <div className="ci-seg">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`ci-seg-btn${value === o.id ? " ci-seg-btn--on" : ""}`}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CompetitorRow({ row, rank, showMovement, expanded, onToggle }: {
  row: RankingRow; rank: number; showMovement: boolean; expanded: boolean; onToggle: () => void;
}) {
  const unmeasured = row.primary_rec == null;
  const mv = formatMovement(row.deltas?.primary_rec);
  return (
    <div className="ci-row-wrap">
      <div className={`ci-trow${showMovement ? " ci-trow--mv" : ""}`} onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}>
        <div className="ci-trow-name">
          <span className="ci-mono-rank">{rank}</span>
          <span className="ci-name">{row.brand}</span>
          {unmeasured && <span className="ci-chip-dashed">Not yet measured</span>}
        </div>
        <div className="ci-num">{formatRate(row.primary_rec, "competitor") ?? ""}</div>
        <div className="ci-num">{formatRate(row.top5, "competitor") ?? ""}</div>
        <div className="ci-num">{formatRate(row.mention, "competitor") ?? ""}</div>
        {showMovement && <div className="ci-num">{mv}</div>}
        <div className="ci-chev">{expanded ? "⌄" : "›"}</div>
      </div>

      {expanded && <RowExpansion row={row} />}
    </div>
  );
}

/**
 * The expanded ranking row (phase 12d), repainted to the CD expanded-panel
 * design: one flat panel, and the evidence inside it is flat too. The previous
 * build nested a card (--card, accent bar) inside the --muted panel, which read
 * as boxes within boxes.
 */
function RowExpansion({ row }: { row: RankingRow }) {
  const reasons = row.why_ahead ?? [];

  // Proportion wording is tiered off the real win shares. When every surviving
  // reason lands in the same tier, the sentence is said ONCE for the group:
  // repeating "cited in some of their wins" under each card was noise, not
  // three separate measurements.
  const tiers = reasons.map((r) => shareTier(r.win_share));
  const uniformTier = tiers.length > 1 && tiers.every((t) => t === tiers[0]);

  return (
    <div className="ci-expand">
      <div className="ci-sub">Why they're ahead</div>

      {reasons.length > 0 ? (
        <>
          {uniformTier && <div className="ci-mono-sm ci-tier-group">{shareLabel(tiers[0])}</div>}
          <div className="ci-reasons">
            {reasons.map((r, i) => (
              <div key={r.reason} className="ci-reason">
                <div className="ci-reason-label">{r.reason}</div>
                {!uniformTier && <div className="ci-mono-sm">{shareLabel(tiers[i])}</div>}
                {/* Quotes are deduped upstream, so a receipt appears at most
                    once per expansion. A reason whose only quote was already
                    spent still renders: the finding is real without it. */}
                {r.quote && <div className="ci-quote-text">{r.quote}</div>}
              </div>
            ))}
          </div>
          {row.why_ahead_low_confidence && (
            <div className="ci-mono-sm ci-thin-note">
              Based on {row.win_count} win{row.win_count === 1 ? "" : "s"}, so read these as thin.
            </div>
          )}
        </>
      ) : (
        <div className="ci-muted">
          {row.win_count === 0
            ? "This competitor has not won a primary recommendation in this read, so there is nothing to explain yet."
            : "No reasons were cited in the answers that chose this competitor."}
        </div>
      )}
    </div>
  );
}

type ShareTier = "most" | "half" | "third" | "some";

/** Reasons are reported as a share of that competitor's wins. */
function shareTier(share: number): ShareTier {
  if (share >= 0.75) return "most";
  if (share >= 0.4) return "half";
  if (share >= 0.25) return "third";
  return "some";
}

function shareLabel(tier: ShareTier): string {
  switch (tier) {
    case "most":  return "Cited in most of their wins";
    case "half":  return "Cited in about half of their wins";
    case "third": return "Cited in about a third of their wins";
    default:      return "Cited in some of their wins";
  }
}

function FunnelView({ client, competitors, showMovement }: {
  client?: RankingRow; competitors: RankingRow[]; showMovement: boolean;
}) {
  const stages = [
    { key: "mention", label: "Mention" },
    { key: "top5", label: "Top 5" },
    { key: "primary_rec", label: "Primary rec." },
  ] as const;
  return (
    <div className="ci-funnel">
      <div className="ci-funnel-inner">
        <div className="ci-funnel-labels">
          <div className="ci-funnel-spacer" />
          {stages.map((s) => <div key={s.key} className="ci-funnel-label">{s.label}</div>)}
        </div>
        <div className="ci-funnel-grid" style={{ gridTemplateColumns: `repeat(${competitors.length || 1}, minmax(0,1fr))` }}>
          {competitors.map((c) => (
            <div key={c.brand} className="ci-funnel-col">
              <div className="ci-funnel-head">
                <span className="ci-funnel-name">{c.brand}</span>
                {c.primary_rec == null && <span className="ci-chip-dashed">Not yet measured</span>}
              </div>
              {stages.map((s) => {
                const v = c[s.key];
                return (
                  <div key={s.key} className="ci-funnel-cell">
                    <div className="ci-funnel-num">{formatRate(v, "competitor") ?? ""}</div>
                    <div className="ci-bar"><div className="ci-bar-fill" style={{ width: `${(v ?? 0) * 100}%` }} /></div>
                    {showMovement && <div className="ci-mono-sm">{formatMovement(c.deltas?.[s.key])}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="ci-funnel-rule" />
        <div className="ci-funnel-client">
          <div className="ci-funnel-head">
            <span className="ci-funnel-name ci-funnel-name--me">{client?.brand}</span>
            <span className="ci-chip-teal">Measured brand</span>
          </div>
          {stages.map((s) => {
            const v = client?.[s.key];
            return (
              <div key={s.key} className="ci-funnel-cell">
                <div className="ci-funnel-num ci-funnel-num--me">
                  <span>{formatRate(v, "client") ?? "—"}</span><span className="ci-metric-pct">%</span>
                </div>
                <div className="ci-bar"><div className="ci-bar-fill ci-bar-fill--me" style={{ width: `${(v ?? 0) * 100}%` }} /></div>
                {showMovement && <div className="ci-mono-sm">{formatMovement(client?.deltas?.[s.key])}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
